// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductDescription from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CartMessages } from "@synd/App/Components/MiniCart/MiniCart.helpers";
import Notify from "@synd/App/Components/Notify";
import Tooltip, { TooltipPresentationProps } from "@synd/MobiusOverrides/Tooltip/Tooltip";
import { ProductDescriptionStyles } from "@synd/Overrides/Components/ProductDescription";
import CartLineQuantity, { CartLineQuantityStyles } from "@synd/Overrides/Widgets/Cart/CartLineQuantity";
import Check from "@synd/XDL/Icons/StockCheckInStock";
import React, { FC, useState } from "react";
import { QtyFieldStyles } from "@synd/App/Components/Qty/QtyField";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: CartMessages;
    cart?: Readonly<Cart>;
    showPricing?: boolean;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
    enableVat?: boolean;
    vatPriceDisplay?: string;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    cartLineImage?: ProductImageStyles;
    brand?: LinkPresentationProps;
    descriptionStyles?: ProductDescriptionStyles;
    lineDescriptionWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    cartLineQuantityWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    cartLineQuantityStyles?: QtyFieldStyles;
    basicProductPriceStyles?: ProductPriceStyles;
    removeCartLineLink?: LinkPresentationProps;
    removeConfirmation?: InjectableCss;
    removeConfirmationText?: TypographyProps;
    removeConfirmationButton?: ButtonPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    extendedUnitNetPriceText?: InjectableCss;
    notifyStyles?: TypographyProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: TypographyProps;
    quoteTooltip?: TooltipPresentationProps;
    errorMsgTooltip?: TooltipPresentationProps;
}

export const miniCartLineStyles: MiniCartLineStyles = {};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    cart,
    showPricing,
    enableVat,
    vatPriceDisplay,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    const isInvalidPrice =
        !cartLine.quoteRequired &&
        !cartLine.isPromotionItem &&
        !cartLine.allowZeroPricing &&
        cartLine.pricing?.unitNetPrice === 0 &&
        cartLine.pricing?.unitRegularPrice === 0;

    const minimumOrderQty = (cartLine as any).minimumOrderQty || 1;
    const multipleSaleQty = (cartLine as any).multipleSaleQty || 1;

    const showQtyTooltip = (minimumOrderQty && minimumOrderQty > 1) || (multipleSaleQty && multipleSaleQty > 1);

    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                {parseInt(cartLine.properties.purchaseMoreDiscountQty || "0", 10) > 0 && (
                    <Notify type={"info"} className="light-mode" {...styles.notifyStyles}>
                        <Typography>{translate("Discount Availaable")}: </Typography>{" "}
                        {translate("Pruchase {0} more to recivce a discount.").replace(
                            "{0}",
                            cartLine.properties.purchaseMoreDiscountQty!,
                        )}
                    </Notify>
                )}
                <ProductImage product={cartLine} extendedStyles={styles.cartLineImage} />
                <StyledWrapper {...styles.lineDescriptionWrapper}>
                    <ProductDescription product={cartLine} extendedStyles={styles.descriptionStyles} />
                    <ProductPartNumbers
                        manufacturerItem={cartLine.manufacturerItem}
                        productNumber={cartLine.erpNumber}
                        customerProductNumber={cartLine.customerName}
                        extendedStyles={styles.partNumbersStyles}
                        showProductNumber={false}
                        {...{
                            packSize: (cartLine as any).packSize,
                        }}
                        // XNG-Changes
                        showManufacturerItem={false}
                    />
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <StyledWrapper {...styles.cartLineQuantityWrapper} {...{ showQtyTooltip }}>
                            <CartLineQuantity
                                cart={cart as Cart}
                                editable={true}
                                extendedStyles={styles.cartLineQuantityStyles}
                                onQtyOrderedChange={(inputQty: number) => {
                                    qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                                }}
                            />
                            {!!errorMessages?.length && (
                                <StyledWrapper {...styles.errorMessagesWrapper}>
                                    {errorMessages.map(errorMessage => (
                                        <Tooltip
                                            key={errorMessage.message}
                                            triggerComponent={
                                                <IconMemo
                                                    src={errorMessage.icon}
                                                    size={16}
                                                    color={errorMessage.color}
                                                />
                                            }
                                            text={errorMessage.message}
                                            {...styles.errorMsgTooltip}
                                        />
                                    ))}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        {showPricing && cartLine.pricing && !cartLine.quoteRequired && (
                            <Typography
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                                {...{ isInvalidPrice }}
                            >
                                {isInvalidPrice
                                    ? translate("Invalid Price")
                                    : enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                      ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                      : cartLine.pricing.extendedUnitNetPriceDisplay}
                            </Typography>
                        )}
                        {cartLine.quoteRequired && (
                            // Coppied from product price
                            <StyledWrapper {...styles.quoteMessageWrapper}>
                                <Typography {...styles.quoteMessage} data-test-selector="quoteRequiredText">
                                    {translate("Requires Quote")}
                                </Typography>
                                <Tooltip {...styles.quoteTooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <Link
                    onClick={() => {
                        setShowRemoveConfirmation(true);
                    }}
                    data-test-selector="cartline_removeLine"
                    aria-label="Cartline Remove Line"
                    {...styles.removeCartLineLink}
                />
                {showRemoveConfirmation && (
                    <StyledWrapper {...styles.removeConfirmation} aria-expanded={showRemoveConfirmation}>
                        <Typography {...styles.removeConfirmationText}>{translate("Are You Sure?")}</Typography>
                        <Button
                            onClick={() => {
                                removeCartLineClickHandler(cartLine);
                            }}
                            {...styles.removeConfirmationButton}
                            aria-label={translate("Yes, Remove")}
                            sizeVariant="small"
                            icon={{
                                src: Check,
                                position: "left",
                            }}
                        >
                            {translate("Yes, Remove")}
                        </Button>
                        <Button
                            onClick={() => {
                                setShowRemoveConfirmation(false);
                            }}
                            variant="secondary"
                            sizeVariant="small"
                            {...styles.removeConfirmationButton}
                            aria-label={translate("No")}
                            icon={{
                                src: X,
                                position: "left",
                            }}
                        >
                            {translate("No")}
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
